import { useEffect } from 'react';
import { HomePage } from './pages/Home';
import { LoginPage } from './pages/Login';
import { userActions } from './store/user';
import { apiService } from './services/api';
import { RegisterPage } from './pages/Register';
import { bindActionCreators } from '@reduxjs/toolkit';
import { useAppDispatch, useAppSelector } from './hooks';
import { NewPasswordPage } from './pages/ResetPassword/newPassword';
import { ResetPasswordPage } from './pages/ResetPassword/resetPassword';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Toast } from './components/Toast';
import { UsersPage } from './pages/Users';
import { AdminChargePage } from './pages/Admin/Charge';
import { ManyCalendarPage } from './pages/ListManyCalendars';
import { UserRole } from './services/api.types';
import { ReportsPage } from './pages/ListReports';
import { ROUTES } from './utils/constants';
import { Background } from './components/Background';
import { usersService } from './services/users.service';
import { SubjectsPage } from './pages/Subjects';
import { AuthLayout } from './components/Layouts/AuthLayout';
import { StrictPublicLayout } from './components/Layouts/StrictPublicLayout';
import { PendingReportsModal } from './components/PendingReportsModal';

export const App = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user);
  const { login, logout } = bindActionCreators(userActions, dispatch);

  /** Check if the user is logged in and try to refresh the token */
  useEffect(() => {
    async function fetchUser() {
      const response = await usersService.me().catch(() => undefined);

      if (response?.id) {
        login(response);
        return true;
      }
      return false;
    }

    async function exec() {
      const isUserLoggedIn = await fetchUser();
      if (!isUserLoggedIn) {
        await apiService.refresh().catch(() => undefined);
        const succeededRefresh = await fetchUser();
        if (!succeededRefresh) logout();
      }
    }
    exec();
  }, []);

  return (
    <BrowserRouter>
      <Background />
      <Toast />

      {/* POPUPS */}
      {user?.role === UserRole.TEACHER && <PendingReportsModal />}

      <Routes>
        {/* STRICT PUBLIC ROUTES */}
        <Route element={<StrictPublicLayout />}>
          <Route path={ROUTES.auth.login} element={<LoginPage />} />
          <Route path={ROUTES.auth.newPass} element={<NewPasswordPage />} />
          <Route path={ROUTES.auth.resetPass} element={<ResetPasswordPage />} />
        </Route>

        {/* AUTHENTICATED ROUTES */}
        <Route element={<AuthLayout />}>
          {/* COMMON ROUTES */}
          <Route path={ROUTES.home} element={<HomePage />} />
          <Route path={ROUTES.users.me} element={<RegisterPage />} />
          <Route path={ROUTES.calendars.index} element={<ManyCalendarPage />} />

          {/* ADMIN */}
          {user?.role === UserRole.ADMIN && (
            <>
              <Route path={ROUTES.users.index} element={<UsersPage />} />
              <Route path={ROUTES.users.create} element={<RegisterPage />} />
              <Route
                path={ROUTES.users.edit(':id')}
                element={<RegisterPage />}
              />

              <Route path={ROUTES.reports.index} element={<ReportsPage />} />
              <Route path={ROUTES.subjects.index} element={<SubjectsPage />} />
              <Route
                path={ROUTES.payments.index}
                element={<AdminChargePage />}
              />
            </>
          )}

          {/* EMPLOYEE */}
          {user?.role === UserRole.EMPLOYEE && (
            <>
              <Route path={ROUTES.users.index} element={<UsersPage />} />
              <Route path={ROUTES.users.create} element={<RegisterPage />} />
              <Route
                path={ROUTES.users.edit(':id')}
                element={<RegisterPage />}
              />
              <Route path={ROUTES.reports.index} element={<ReportsPage />} />
              <Route
                path={ROUTES.payments.index}
                element={<AdminChargePage />}
              />
              <Route path={ROUTES.subjects.index} element={<SubjectsPage />} />
            </>
          )}

          {/* STUDENT */}
          {user?.role === UserRole.STUDENT && <></>}

          {/* RESPONSIBLE */}
          {user?.role === UserRole.RESPONSIBLE && (
            <Route path={ROUTES.payments.index} element={<AdminChargePage />} />
          )}

          {/* TEACHER */}
          {user?.role === UserRole.TEACHER && (
            <Route path={ROUTES.reports.index} element={<ReportsPage />} />
          )}

          {/* NOT FOUND REDIRECT */}
          <Route path="*" element={<Navigate to={ROUTES.home} />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};
